import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import styles from './legal.module.css';

export default function TermsOfServicePage() {
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; }
    document.body.style.overflow = 'auto';
    return () => { if (root) { root.style.overflow = ''; root.style.height = ''; } document.body.style.overflow = ''; };
  }, []);

  return (
    <div className={styles.container}>
      <>
        <title>Terms of Service | Tasking.tech</title>
        <meta
          name="description"
          content="Terms of Service for Tasking.tech - AI-Powered Secure Code Collaboration Platform"
        />
      </>

      {/* Navigation */}
      <nav className={styles.nav}>
        <div className={styles.navContent}>
          <Link to="/" className={styles.logo}>
            <img
              src="/logo.png"
              alt="Tasking.tech"
              className={styles.logoIcon}
            />
            <span className={styles.logoText}>
              <span>tasking</span>
              <span className={styles.techSuffix}>.tech</span>
            </span>
          </Link>
          <Button variant="ghost" asChild>
            <Link to="/">
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </Button>
        </div>
      </nav>

      <main className={styles.content}>
        <h1>Terms of Service</h1>
        <p className={styles.lastUpdated}>Last updated: February 1, 2025</p>

        <section className={styles.section}>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using Tasking.tech ("the Service"), you agree to be bound by these Terms
            of Service. If you do not agree to these terms, please do not use the Service.
          </p>
        </section>

        <section className={styles.section}>
          <h2>2. Description of Service</h2>
          <p>
            Tasking.tech provides an AI-powered secure code collaboration platform that allows users
            to share, edit, and collaborate on code in real-time within sandboxed environments. The
            Service includes:
          </p>
          <ul>
            <li>Real-time code collaboration sessions</li>
            <li>AI-powered code assistance and generation</li>
            <li>Sandboxed code execution environments</li>
            <li>Secure file sharing and version control</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>3. User Accounts</h2>
          <p>
            To access certain features of the Service, you must create an account. You are
            responsible for:
          </p>
          <ul>
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized use</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>4. Acceptable Use</h2>
          <p>You agree not to use the Service to:</p>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe upon intellectual property rights</li>
            <li>Upload or execute malicious code</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Interfere with other users' use of the Service</li>
            <li>Generate or distribute harmful, illegal, or offensive content</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>5. Intellectual Property</h2>
          <p>
            You retain all rights to the code and content you create or upload to the Service. By
            using the Service, you grant us a limited license to host, display, and process your
            content as necessary to provide the Service.
          </p>
          <p>
            The Tasking.tech platform, including its design, features, and underlying technology,
            remains the property of Tasking.tech.
          </p>
        </section>

        <section className={styles.section}>
          <h2>6. AI-Generated Content</h2>
          <p>
            The Service includes AI-powered features that generate code suggestions and content. You
            understand that:
          </p>
          <ul>
            <li>AI-generated content may require review and modification</li>
            <li>We do not guarantee the accuracy or suitability of AI outputs</li>
            <li>You are responsible for reviewing and validating AI-generated code</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>7. Privacy</h2>
          <p>
            Your use of the Service is also governed by our{' '}
            <Link to="/privacy-policy">Privacy Policy</Link>, which describes how we collect, use,
            and protect your information.
          </p>
        </section>

        <section className={styles.section}>
          <h2>8. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Tasking.tech shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages arising from your use
            of the Service.
          </p>
        </section>

        <section className={styles.section}>
          <h2>9. Modifications to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify users of
            significant changes via email or through the Service. Continued use of the Service after
            changes constitutes acceptance of the modified Terms.
          </p>
        </section>

        <section className={styles.section}>
          <h2>10. Termination</h2>
          <p>
            We may suspend or terminate your access to the Service at any time for violation of
            these Terms or for any other reason at our discretion. Upon termination, your right to
            use the Service will immediately cease.
          </p>
        </section>

        <section className={styles.section}>
          <h2>11. Contact Us</h2>
          <p>If you have any questions about these Terms of Service, please contact us at:</p>
          <p>
            <a href="mailto:legal@tasking.tech">legal@tasking.tech</a>
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <img
              src="/logo.png"
              alt="Tasking.tech"
              className={styles.footerLogo}
            />
            <span>
              tasking<span className={styles.techSuffix}>.tech</span>
            </span>
          </div>
          <div className={styles.footerLinks}>
            <Link to="/docs">Docs</Link>
            <Link to="/security">Security</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
            <Link to="/">Home</Link>
          </div>
          <span className={styles.copyright}>© 2026 Tasking.tech. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
